import { createFreePreviewMember } from 'actions/LuxPlusActions'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import FormatCurrency from 'components/Common/FormatCurrency'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import Places from 'constants/places'
import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getImageUrl } from 'lib/image/imageUtils'
import { getSearchQueryFromFilter } from 'lib/search/searchUtils'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import LuxPlusLandingPageBenefit from 'luxPlus/components/LuxPlusLandingPageBenefit'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import useEnabledLuxPlusBenefits from 'luxPlus/hooks/useEnabledLuxPlusBenefits'
import { getCanRedeemLuxPlusBenefits, getCanViewLuxPlusBenefits, getIsFreePreviewLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import { selectLoggedIn } from 'selectors/accountSelectors'
import styled from 'styled-components'
import { LUX_PLUS_MONTH_CAMPAIGN_ID } from '../constants/luxPlusMonthTakeoverConstants'

const FILTERS: App.OfferListFilters = {
  offerTypes: ['hotel', 'tactical_ao_hotel'],
  luxPlusFeatures: ['Member prices'],
  campaigns: [LUX_PLUS_MONTH_CAMPAIGN_ID],
  destinations: [Places.Anywhere.id],
  sortBy: 'luxPlusList',
  limit: 20,
}

const LUX_PLUS_MONTH_HOTELS_OFFER_CAROUSEL_ALL_LINK = `/search?${getSearchQueryFromFilter(FILTERS)}`

const Hero = styled(Sheet)`
  overflow: hidden;
  background:
  url(${getImageUrl('lux-plus-month-plus-texture-mobile')}) no-repeat bottom / 100%,
    radial-gradient(204.82% 98.86% at 1.87% 98.86%, #0C156E 0%, #0F1476 10.78%, #12158A 20.52%, #171891 30.69%, #202199 38.13%, #1B1E9B 45%, #1923A7 52.41%, #202CAE 58.12%, #2A36B4 66.26%, #3645BE 75.17%, #4054CB 83.63%, #4866DE 100%);


  ${mediaQueryUp.tablet} {
    background:
      url(${getImageUrl('lux-plus-month-plus-texture-desktop')}) no-repeat bottom center / ${rem(1440)},
      radial-gradient(circle at top left, rgb(20, 22, 131) 0%, 14.2729%, rgb(36, 29, 147) 28.5458%, 40.6643%, rgb(50, 49, 166) 52.7828%, 59.7846%, rgb(62, 77, 188) 66.7864%, 71.6338%, rgb(66, 91, 198) 76.4812%, 80.1616%, rgb(72, 102, 207) 83.842%, 88.5996%, rgb(82, 130, 229) 93.3573%, 96.6786%, rgb(87, 148, 242) 100%);
  }
`

const BenefitsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${rem(156)}, 1fr));
  column-gap: ${rem(24)};
  row-gap: ${rem(48)};
  justify-content: space-between;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(auto-fit, minmax(${rem(246)}, 1fr));
    row-gap: ${rem(32)};
  }
`

const TopContentContainer = styled(LayoutContainer)`
  padding-block: ${rem(40)} ${rem(56)};
`

const BottomContentContainer = styled(LayoutContainer)`
  padding-inline: 0;
  display: grid;
  grid-template:
    "tiles-container" ${rem(224)}
    "content" auto / 1fr;

  ${mediaQueryUp.tablet} {
    grid-template: "content tiles-container" 1fr / minmax(auto, ${rem(588)}) minmax(${rem(200)}, 1fr);
    justify-content: space-between;
  }
`

const TilesContainer = styled.div`
  grid-area: tiles-container;
  position: relative;
  overflow: visible;
  align-self: stretch;
  pointer-events: none;
`

const Tiles = styled.div`
  background: url(${getImageUrl('ejjeqygm58u0g6o5tjxp')}) no-repeat bottom center / contain;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  height: ${rem(229)};
  width: ${rem(494)};

  ${mediaQueryUp.tablet} {
    background: url(${getImageUrl('ju5nsz1jtqirx9z1i5id')}) no-repeat bottom left / contain;
    top: 0;
    left: 0;
    transform: none;
    height: ${rem(559)};
    width: ${rem(697)};
  }
`

const BottomContent = styled(LayoutContainer)`
  grid-area: content;
  align-self: end;
  padding-block: ${rem(40)};

  ${mediaQueryUp.tablet} {
    padding-block: ${rem(80)};
  }
`

function LuxPlusMonthLandingPageBenefitsHero() {
  const subscriptionProduct = useAppSelector(state => state.luxPlus.products.subscription)
  const offer = useMemo(() => subscriptionProduct.offers?.find(offer => !offer.joinFeeWaived), [subscriptionProduct.offers])
  const enabledBenefits = useEnabledLuxPlusBenefits(offer?.benefits)

  const isLoggedIn = useAppSelector(selectLoggedIn)
  const showModal = useContext(ModalContext)
  const openLoginModal = useCallback(() => showModal<AccountAccessModalResult>(<AccountAccessModal />), [showModal])

  const dispatch = useAppDispatch()
  const startFreePreview = useCallback(async() => {
    if (!isLoggedIn) {
      const loginResult = await openLoginModal()
      if (loginResult.loggedIn) {
        dispatch(createFreePreviewMember())
      }
    } else {
      dispatch(createFreePreviewMember())
    }
  }, [dispatch, isLoggedIn, openLoginModal])

  const isLuxPlusMember = useAppSelector(getCanRedeemLuxPlusBenefits)
  const isFreePreviewMember = useAppSelector(getIsFreePreviewLuxPlusMember)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)

  return <Hero paletteType="inverse">
    <TopContentContainer>
      <Group direction="vertical" gap={64}>
        <Group direction="vertical" gap={40}>
          <Group direction="vertical" gap={8}>
            <Heading variant="heading3">
              Unlimited benefits all year round
            </Heading>
            <BodyText variant="medium">
              As a {LUXURY_PLUS.PROGRAM_NAME} member you'll enjoy unlimited benefits all year round for{' '}
              {!!offer && <FormatCurrency value={offer.price} />}
              {!offer && <TextLoadingBox width="4ch" />}
              {' '}per year, and a one-off joining fee of{' '}
              {!!offer && <FormatCurrency value={offer.joinFee} />}
              {!offer && <TextLoadingBox width="4ch" />}
              .
            </BodyText>
            {(!isLuxPlusMember && !!offer) && <BodyText variant="medium" colour="highlight-primary">
              Special offer: No joining fee when you purchase an eligible {LUXURY_PLUS.PROGRAM_NAME} offer (save <FormatCurrency value={offer.joinFee} />)
            </BodyText>}
          </Group>
          <BenefitsContainer>
            {enabledBenefits.map((benefit) => <LuxPlusLandingPageBenefit key={benefit.title} benefit={benefit} noCta />)}
          </BenefitsContainer>
        </Group>
        {isLuxPlusMember && <BodyText variant="small">
          Your {LUXURY_PLUS.PROGRAM_NAME} membership will renew automatically on your anniversary date. We'll send you a reminder. You can cancel your renewal at anytime.{' '}
          <TextLink to={`/${LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE}`} target="_blank">T&Cs apply</TextLink>
        </BodyText>}
      </Group>
    </TopContentContainer>
    <BottomContentContainer>
      <BottomContent>
        <Group direction="vertical" gap={32}>
          <Group direction="vertical" gap={20}>
            <Heading variant="heading2">
              {canViewLuxPlusBenefits && <>
                {isLuxPlusMember && <>Your VIP travel club, <i>elevated</i>.</>}
                {isFreePreviewMember && <>Our VIP travel club, <i>elevated</i>.</>}
              </>}
              {!canViewLuxPlusBenefits && <>Browse like a VIP for LuxPlus<Colour value="highlight-lux-plus">+</Colour> month</>}
            </Heading>
            <Heading variant="heading4">
              {canViewLuxPlusBenefits && <>
                {isLuxPlusMember && <>More savings, more hidden offers, more VIP benefits - more reasons to keep exploring.</>}
                {isFreePreviewMember && <>More savings, more hidden offers, more VIP benefits - more reasons to join today.</>}
              </>}
              {!canViewLuxPlusBenefits && <>
                Unlock hidden prices, view member-only offers and see what VIP travel is really like. No credit card needed.
              </>}
            </Heading>
          </Group>
          <Group direction="vertical" gap={24} tabletHorizontalAlign="start">
            {!canViewLuxPlusBenefits && <TextButton kind="primary" shape="round" size="large" onClick={startFreePreview}>
              Start your FREE preview today
            </TextButton>}
            {canViewLuxPlusBenefits && <TextButton kind="primary" shape="round" size="large" to={LUX_PLUS_MONTH_HOTELS_OFFER_CAROUSEL_ALL_LINK}>
              Explore top hotel offers
            </TextButton>}
            {!isLoggedIn && <BodyText variant="medium" align="center" tabletAlign="start">
              Already a {LUXURY_PLUS.PROGRAM_NAME} member? <TextLink onClick={openLoginModal}>Sign in</TextLink>
            </BodyText>}
            {(isLoggedIn && isLuxPlusMember) && <BodyText variant="medium" align="center" tabletAlign="start">
              <TextLink to={`/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`}>Manage membership</TextLink>
            </BodyText>}
          </Group>
        </Group>
      </BottomContent>
      <TilesContainer role="presentation">
        <Tiles role="presentation" />
      </TilesContainer>
    </BottomContentContainer>
  </Hero>
}

export default LuxPlusMonthLandingPageBenefitsHero
